import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  Settings as SettingsIcon, Users, Shield, Database,
  ScrollText, AlertTriangle, Info, Calendar, Eye, Activity, Mail, Library,
  Smartphone,
} from 'lucide-react';
import { Badge } from '../../components/ui/Badge';

import GeneralTab from './GeneralTab';
import UtilisateursTab from './UtilisateursTab';
import PermissionsTab from './PermissionsTab';
import SauvegardesTab from './SauvegardesTab';
import AuditLogTab from './AuditLogTab';
import DonneesTab from './DonneesTab';
import { AnneeAcademique as AnneeAcademiqueTab } from '../Annee/AnneeAcademique';
import SurveillanceTab from './SurveillanceTab';
import VisibiliteOngletsTab from './VisibiliteOngletsTab';
import MessagerieTab from './MessagerieTab';
import RapportsOperationsTab from './RapportsOperationsTab';
import AppsMobilesTab from './AppsMobilesTab';

type TabKey = 'general' | 'annees' | 'utilisateurs' | 'permissions' | 'visibilite' | 'surveillance' | 'messagerie' | 'apps_mobiles' | 'sauvegardes' | 'audit' | 'rapports_ops' | 'donnees';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'general', label: 'Général', icon: <SettingsIcon size={16} /> },
  { key: 'annees', label: 'Années Académiques', icon: <Calendar size={16} /> },
  { key: 'utilisateurs', label: 'Utilisateurs', icon: <Users size={16} /> },
  { key: 'permissions', label: 'Permissions', icon: <Shield size={16} /> },
  { key: 'visibilite', label: 'Visibilité Onglets', icon: <Eye size={16} /> },
  { key: 'surveillance', label: 'Surveillance', icon: <Activity size={16} /> },
  { key: 'messagerie', label: 'Messagerie', icon: <Mail size={16} /> },
  { key: 'apps_mobiles', label: 'Apps Mobiles', icon: <Smartphone size={16} /> },
  { key: 'sauvegardes', label: 'Sauvegardes', icon: <Database size={16} /> },
  { key: 'audit', label: "Journal d'Audit", icon: <ScrollText size={16} /> },
  { key: 'rapports_ops', label: "Rapports d'Opérations", icon: <Library size={16} /> },
  { key: 'donnees', label: 'Données', icon: <AlertTriangle size={16} /> },
];

const Settings: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [activeTab, setActiveTab] = useState<TabKey>('general');

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  if (!isAdmin) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Paramètres</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Informations du compte</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Info size={18} className="text-blue-500" />
            Session
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Statut</p>
              <p className="mt-1 font-medium text-green-600 dark:text-green-400">Connecté</p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Utilisateur</p>
              <p className="mt-1 font-medium text-gray-900 dark:text-gray-100">{user?.username || '—'}</p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Rôle</p>
              <p className="mt-1 font-medium text-gray-900 dark:text-gray-100">{user?.role || '—'}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info size={18} className="text-blue-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-300">Accès limité</h4>
              <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                Les paramètres avancés sont réservés aux administrateurs. Contactez un administrateur pour modifier les réglages système.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Paramètres</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Configuration système et administration
          </p>
        </div>
        <Badge variant="info">{user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}</Badge>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-0 -mb-px overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div>
        {activeTab === 'general' && <GeneralTab />}
        {activeTab === 'annees' && <AnneeAcademiqueTab />}
        {activeTab === 'utilisateurs' && <UtilisateursTab />}
        {activeTab === 'permissions' && <PermissionsTab />}
        {activeTab === 'visibilite' && <VisibiliteOngletsTab />}
        {activeTab === 'surveillance' && <SurveillanceTab />}
        {activeTab === 'messagerie' && <MessagerieTab />}
        {activeTab === 'apps_mobiles' && <AppsMobilesTab />}
        {activeTab === 'sauvegardes' && <SauvegardesTab />}
        {activeTab === 'audit' && <AuditLogTab />}
        {activeTab === 'rapports_ops' && <RapportsOperationsTab />}
        {activeTab === 'donnees' && <DonneesTab />}
      </div>
    </div>
  );
};

export default Settings;
